//UIカード編集画面-デザインタブ
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { DraggableData } from 'react-draggable';
import { ThemeProvider } from '@material-ui/core/styles';
import { TextField, Button, Grid, Container, Box, Tabs, Tab, FormControlLabel, Checkbox, RadioGroup } from '@material-ui/core';
import { Menu, MenuItem } from '@material-ui/core';
import { Radio } from '@material-ui/core';
import InputIcon from '@material-ui/icons/InputOutlined';
import ImageIcon from '@material-ui/icons/Image';
import ImageOutlinedIcon from '@material-ui/icons/ImageOutlined';
import TextFieldIcon from '@material-ui/icons/TextFields';
import WrapTextIcon from '@material-ui/icons/WrapText';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import GridOnIcon from '@material-ui/icons/GridOn';
import DropDownIcon from '@material-ui/icons/ArrowDropDownCircle';
import ButtonIcon from '@material-ui/icons/Crop169Rounded';
import useWindowSize from '@rehooks/window-size';
import { nanoid } from 'nanoid';
import { useDispatch } from 'react-redux';

import { CardDb } from '../redux/cardDbSlice';
import { generateImageId } from '../redux/imageDbSlice';
import { UiCardItemType, InputUiSketchItem, TextUiSketchItem, ButtonUiSketchItem, CheckboxUiSketchItem, DropdownUiSketchItem, MultiInputUiSketchItem, VariableImageUiSketchItem, StaticImageUiSketchItem, GroupUiSketchItem, UiCardItemDb, generateUiCardItemId } from '../redux/uiCardItemDbSlice';
import { FontType } from '../redux/cardAttributeModelIndex';
import useCurrentUserId from '../hooks/useCurrentUserId';
import useLatelyImages from '../hooks/useLatelyImages';
import { deepCopy, FullSizeImageAddType, getImgSize, updateImageDbs } from '../general';
import { initial } from './index';
import TabPanel, { TabsAppBar, tabsIndicatorTheme, a11yProps } from '../components/TabPanel';
import SelectUiItemName from '../components/SelectUiItemName';
import RenderUiCard from '../components/RenderUiCard';
import EditItemFont from '../components/EditItemFont';
import ItemNameInput from '../components/ItemNameInput';
import EditItemPosition from '../components/EditItemPosition';
import { EditUiCardItemDbHashType } from '../components/EditDialog';
import DrawToolDialog from '../components/DrawToolDialog';
import ImageBrowserDialog from '../components/ImageBrowserDialog';
import ColorPalette from '../components/ColorPalette';

const ItemField = styled(TextField)`
  width: 100%;
`
const MenuButton = styled(Button)`
  size: 'large';
  width: 100%;
  height: 60px;
`
const SmallSpan = styled.span`
  font-size: 5pt;
`
const ColorLabel = styled.label`
  margin-right: 8px;
`

//デザインタブ-入力項目選択メニュー
type ItemMenuAreaProps = {
  addNewItem: (kind: string, image?: FullSizeImageAddType) => void;
  onImageBrowser: () => void;
  onDrawTool: () => void;
}
const ItemMenuArea: React.FC<ItemMenuAreaProps> = ({addNewItem, onImageBrowser, onDrawTool}) => {
  const dispatch = useDispatch();
  const userId = useCurrentUserId();
  const [imageAnchorEl, setImageAnchorEl] = React.useState<null | HTMLElement>(null);
  const [latelyIds, , latelyImagesId] = useLatelyImages();

  const imageHandleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setImageAnchorEl(event.currentTarget)
  }
  const imageHandleClose = () => {
    setImageAnchorEl(null)
  }
  const imageBrowserOpen = () => {
    imageHandleClose()
    onImageBrowser()
  }
  const openUserFileSelector = () => {
    imageHandleClose()
    const selector = document.getElementById('user-file-selector');
    if (!selector) return;
    selector.click();
  }
  const getFile = (ev: React.ChangeEvent<HTMLInputElement>) => {
    if (!ev.target.files) return;
    const file = ev.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (typeof reader.result !== 'string') return;
      const dataUrlBase64 = reader.result;
      const [width, height] = getImgSize(dataUrlBase64);
      const imageId = generateImageId();
      updateImageDbs({ dispatch, imageId, dataUrlBase64, userId, latelyIds, latelyImagesId });
      addNewItem('static', { id: imageId, width, height });
    }
    ev.currentTarget.value = '';
  }
  const drawToolOpen = () => {
    imageHandleClose();
    onDrawTool();
  }

  return (
    <Box>
      <MenuButton onClick={() => addNewItem('input')}><InputIcon /><SmallSpan><pre>テキスト<br/>入力</pre></SmallSpan></MenuButton>
      <MenuButton onClick={() => addNewItem('multi')}><WrapTextIcon /><SmallSpan><pre> 複数行 <br/>テキスト</pre></SmallSpan></MenuButton>
      <MenuButton onClick={() => addNewItem('text')}><TextFieldIcon /><SmallSpan><pre>テキスト<br/>表示</pre></SmallSpan></MenuButton>
      <MenuButton onClick={() => addNewItem('button')}><ButtonIcon /><SmallSpan><pre> ボタン </pre></SmallSpan></MenuButton>
      <MenuButton onClick={() => addNewItem('checkbox')}><CheckBoxIcon /><SmallSpan><pre>チェック<br/>ボックス</pre></SmallSpan></MenuButton>
      <MenuButton onClick={() => addNewItem('dropdown')}><DropDownIcon /><SmallSpan><pre>ドロップ<br/>ダウン</pre></SmallSpan></MenuButton>
      <MenuButton onClick={() => addNewItem('variable')}><ImageIcon /><SmallSpan><pre>可変画像</pre></SmallSpan></MenuButton>
      <MenuButton aria-controls="image-menu" aria-haspopup="true" onClick={imageHandleClick}>
        <ImageOutlinedIcon /><SmallSpan><pre>固定画像</pre></SmallSpan>
      </MenuButton>
      <Menu id="image-menu" anchorEl={imageAnchorEl} keepMounted
        open={Boolean(imageAnchorEl)}
        onClose={imageHandleClose}
      >
        <MenuItem onClick={imageBrowserOpen}>画像ブラウザ...</MenuItem>
        <MenuItem onClick={openUserFileSelector} id="user-file-selector-button">
          <input type="file" accept="image/png, image/jpeg" id="user-file-selector" onChange={getFile} style={{display: 'none'}} />ファイルから挿入...
        </MenuItem>
        <MenuItem onClick={drawToolOpen}>絵を描いて挿入...</MenuItem>
      </Menu>
      <MenuButton onClick={() => addNewItem('group')}><GridOnIcon /><SmallSpan><pre>グループ</pre></SmallSpan></MenuButton>
    </Box>
  )
}

//デザインタブ-横/縦表示切替
type CardOrientationProps = {
  card: CardDb;
  setCard: (card: CardDb) => void;
}
const CardOrientation: React.FC<CardOrientationProps> = ({card, setCard}) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const orientation = (event.target as HTMLInputElement).value;
    if (orientation === 'horizontal' || orientation === 'vertical') {
      const currentWidth = orientation === 'horizontal' ? initial.cardWidth : initial.cardHeight;
      const currentHeight = orientation === 'horizontal' ? initial.cardHeight : initial.cardWidth;
      const newCard: CardDb = { ...deepCopy(card), orientation, currentHeight, currentWidth };
      setCard(newCard);
    }
  }

  return (
    <RadioGroup value={card.orientation} onChange={handleChange} style={{width: 'max-content'}}>
      <FormControlLabel value="horizontal" control={<Radio color="primary" />} label="ヨコ長" checked={card.orientation !== 'vertical'} />
      <FormControlLabel value="vertical" control={<Radio color="primary" />} label="タテ長" />
    </RadioGroup>
  );
}

type CardDisplayControlProps = {
  card: CardDb;
  setCard: (card: CardDb) => void;
}
const CardDisplayControl: React.FC<CardDisplayControlProps> = ({card, setCard}) => {
  const changeShadowDisplay = (display: boolean) => {
    const newCard = deepCopy(card);
    newCard.shadow = display;
    setCard(newCard);
  }

  return (
    <Grid container spacing={0}>
      <Grid container item xs={12}>
        <Grid item alignItems="center" xs={12} md={card.orientation === 'vertical' ? 12 : 6}>
          <Grid container alignItems="center">
            <FormControlLabel label="枠線表示"
              control={
                <Checkbox checked={card.frame.display} color="primary"
                  onChange={(ev) => setCard({...card, frame: {...card.frame, display: ev.currentTarget.checked}})} />
              } />
            <ColorPalette
              currentValue={card.frame.color}
              onChange={value => setCard({ ...card, frame: { ...card.frame, color: value } })}
              disabled={!card.frame.display}
            />
          </Grid>
        </Grid>
        <Grid item alignItems="center" xs={12} md={card.orientation === 'vertical' ? 12 : 6}>
          <FormControlLabel label="影あり"
            control={
              <Checkbox checked={card.shadow} color="primary"
                onChange={(ev) => changeShadowDisplay(ev.currentTarget.checked)} />
          } />
        </Grid>
      </Grid>
    </Grid>
  );
}

//デザインタブ-カード表示
type CardDesignAreaProps = {
  cardId: string;
  card: CardDb;
  setCard: (card: CardDb) => void;
  items: EditUiCardItemDbHashType;
  changeItem: (item: UiCardItemDb | null, id?: string) => void;
  selectingId: string | undefined;
  setSelectingId: (id: string | undefined) => void;
  changeTab: () => void;
}
const CardDesignArea: React.FC<CardDesignAreaProps> = ({cardId, card, setCard, items, changeItem, selectingId, setSelectingId, changeTab}) => {
  const windowSize = useWindowSize();
  const workWidth = (windowSize.innerWidth - (64 * 2)) * 0.6;// Dialogのwidth*0.6にしたいけど・・やり方がわからない
  const workHeight = (windowSize.innerHeight - (64 * 2)) * 0.5;
  const widthScale = workWidth / initial.uiSketchWide;
  const heightScale = workHeight / initial.uiSketchNarrow;
  let scaleValue = widthScale <= heightScale ? widthScale : heightScale;

  if (!card) return <></>;
  return (
    <Box ml={1}>
      { card.orientation !== 'vertical' &&
        <div>
          <Box>
            <CardOrientation card={card} setCard={setCard} />
          </Box>
          <RenderUiCard cardId={cardId} card={card} 
            items={items} changeItem={changeItem}
            selectingId={selectingId} setSelectingId={setSelectingId} 
            scaleValue={scaleValue} workHeight={workHeight}
            changeTab={changeTab}
          />
          <Box mt={1}>
            <CardDisplayControl card={card} setCard={setCard} />
          </Box>
        </div>
      }
      { card.orientation === 'vertical' &&
        <Grid container>
          <Grid item xs={8}>
            <RenderUiCard cardId={cardId} card={card} 
              items={items} changeItem={changeItem}
              selectingId={selectingId} setSelectingId={setSelectingId} 
              scaleValue={scaleValue} workHeight={workHeight}
              changeTab={changeTab}
            />
          </Grid>
          <Grid item xs={4}>
            <CardOrientation card={card} setCard={setCard} />
            <Box mt={4}>
              <CardDisplayControl card={card} setCard={setCard} />
            </Box>
          </Grid>
        </Grid>
      }
    </Box>
  );
}

export type ListItemType = { name: string; type: UiCardItemType; parentId: string; z: number; order: number; childrenIds: string[]; }
export type ListHash = { [key in string]: ListItemType; }
export type SubListHash = { [key in string]: ListHash; }

//デザインタブ-アイテム/詳細タブ切り替え
type ItemDesignAreaProps = {
  cardId: string;
  items: EditUiCardItemDbHashType;
  setItems: (items: EditUiCardItemDbHashType) => void;
  changeItem: (item: UiCardItemDb | null, id?: string) => void;
  selectingId: string | undefined;
  setSelectingId: (id: string | undefined) => void;
  tabValue: number;
  setTabValue: (tabValue: number) => void;
}
const ItemDesignArea: React.FC<ItemDesignAreaProps> = ({cardId, items, setItems, changeItem, selectingId, setSelectingId, tabValue, setTabValue}) => {
  const ids = Object.keys(items);
  const [originList, setOriginList] = useState<ListHash>({});
  const [list, setList] = useState<ListHash>({});
  const selectingItem = typeof selectingId === 'string' ? items[selectingId] : null;
  const windowSize = useWindowSize()
  const workHeight = windowSize.innerHeight - (64 * 2) - 130

  useEffect(() => {
    const parentList: ListHash = {};
    const childrenLists: SubListHash = {};
    ids.forEach(id => {
      const item = items[id];
      if (!item) return;
      if (item.parentId === cardId) parentList[id] = { name: item.name, type: item.type, parentId: item.parentId, z: item.z, order: item.z, childrenIds: [] };
      else {
        if (!childrenLists[item.parentId]) childrenLists[item.parentId] = {};
        childrenLists[item.parentId][id] = { name: item.name, type: item.type, parentId: item.parentId, z: item.z, order: item.z, childrenIds: [] };
      }
    })
    ids.forEach(id => {
      const item = items[id];
      if (!item || item.parentId === cardId) return;
      const parent = parentList[item.parentId];
      if (!parent) return;
      parent.childrenIds.push(id);
      parentList[item.parentId] = parent;
    })

    const newIds = Object.keys(parentList).sort((a, b) => parentList[a].z - parentList[b].z);
    const childrenListIds = Object.keys(childrenLists);

    ids.forEach(id => {
      const item = items[id];
      const childList = childrenLists[id] ? deepCopy(childrenLists[id]) : null;
      if (!item || !childList) return;
      const childIds = Object.keys(childList).sort((a, b) => childList[a].z - childList[b].z);
      const preIx = newIds.findIndex(parentId => parentId === id);
      if (childIds.length === 0 || preIx < 0) return;
      newIds.splice(preIx + 1, 0, ...childIds);
    })
    childrenListIds.forEach(id => {
      const childList = childrenLists[id];
      if (!childList) return;
      const childIds = Object.keys(childList);
      childIds.forEach(childId => {
        const childItem = childList[childId];
        if (!childItem) return;
        parentList[childId] = childItem;
      })
    })

    const updateOrder = (iIds: string[], iList: ListHash) => {
      iIds.forEach((id, ix) => {
        const item = iList[id];
        if (!item) return;
        iList[id] = { ...item, order: ix };
      })
    }
    updateOrder(newIds, parentList);

    setOriginList(parentList);
    setList(parentList);
  }, [items])

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setTabValue(newValue)
  }
  const deleteItem = () => {
    if (!selectingId || !selectingItem) return;
    const newItems = deepCopy(items);
    // update other item.z
    ids.forEach(id => {
      const item = items[id];
      if (item && item.parentId === selectingId) newItems[id] = null;
      else if (item && item.parentId === selectingItem.parentId && item.z > selectingItem.z) newItems[id] = { ...item, z: item.z - 1 };
    })
    // delete selecting item
    newItems[selectingId] = null;
    setTabValue(0);
    setSelectingId(undefined);
    setItems(newItems);
  }
  const changeItemZ = (data: DraggableData) => {
    if (typeof selectingId !== 'string') return;
    const newItems = deepCopy(items);
    const targetItem = deepCopy(selectingItem);
    const targetListItem = originList[selectingId];
    const dragY = Math.round(data.lastY / 48);
    const dropY = Math.min(Math.round(data.y / 48), ids.length - 1);
    const detailedDropY = data.y / 48;
    if (!targetItem || !targetListItem || dragY === detailedDropY) return;

    let parentGroup = { changing: false, id: '', lastParent: targetItem.parentId };
    const overlapCondition = detailedDropY > dragY ? -0.5 : 0;
    if (targetItem.type !== 'group') {
      ids.forEach(id => {
        const item = items[id];
        const listItem = originList[id];
        if (id === selectingId || !item || !listItem) return;
        const parentRangeTop = listItem.order + overlapCondition;
        const parentRangeBottom = parentRangeTop + 0.5 + listItem.childrenIds.length;
        if (item.type === 'group' && parentRangeTop <= detailedDropY && detailedDropY < parentRangeBottom) {
          if (id === parentGroup.lastParent) parentGroup = { ...parentGroup, changing: false, id: id };
          else parentGroup = { ...parentGroup, changing: true, id: id };
        }
      })
    }
    if (parentGroup.id === '' && parentGroup.lastParent !== cardId) parentGroup = { ...parentGroup, changing: true, id: cardId };
    else if (parentGroup.id === '' && parentGroup.lastParent === cardId) parentGroup = { ...parentGroup, id: cardId };

    if (dragY === dropY && !parentGroup.changing) return;
    const childrenLength = targetListItem.childrenIds.length;

    let dropChecked = false;
    let newZ = 0;
    ids.forEach(id => {
      const item = items[id];
      const listItem = originList[id];
      if (id === selectingId || !item || !listItem) return;
      const targetOrderTop = listItem.order;
      const targetOrderBottom = targetOrderTop + listItem.childrenIds.length;

      if (dragY < dropY && !parentGroup.changing) {
        dropChecked = true;
        if (item.parentId !== parentGroup.id) return;
        if (dragY < targetOrderBottom && targetOrderBottom <= dropY + childrenLength) {
          newItems[id] = { ...item, z: item.z - 1 };
          targetItem.z++;
          newItems[selectingId] = targetItem;
        }
      } else if (dragY > dropY && !parentGroup.changing) {
        dropChecked = true;
        if (item.parentId !== parentGroup.id) return;
        if (dropY <= targetOrderTop && targetOrderTop < dragY) {
          newItems[id] = { ...item, z: item.z + 1 };
          targetItem.z--;
          newItems[selectingId] = targetItem;
        }
      } else if (dragY < detailedDropY && parentGroup.changing) {
        if (item.parentId === parentGroup.lastParent) {
          if (targetOrderBottom > dragY) newItems[id] = { ...item, z: item.z - 1 };
        }
        else if (item.parentId === parentGroup.id) {
          if (targetOrderBottom > dropY) newItems[id] = { ...item, z: item.z + 1 };
          else if (targetOrderBottom <= dropY) {
            newZ++;
            newItems[selectingId] = { ...targetItem, parentId: parentGroup.id, z: newZ };
            dropChecked = true;
          }
        }
      } else if (dragY > dropY && parentGroup.changing) {
        if (item.parentId === parentGroup.lastParent) {
          if (targetOrderTop > dragY) newItems[id] = { ...item, z: item.z - 1 };
        }
        else if (item.parentId === parentGroup.id) {
          if (targetOrderTop > dropY) newItems[id] = { ...item, z: item.z + 1 };
          else if (targetOrderTop === dropY) {
            newItems[id] = { ...item, z: item.z + 1 };
            newItems[selectingId] = { ...targetItem, parentId: parentGroup.id, z: newZ };
            dropChecked = true;
          }
          else if (targetOrderTop < dropY) {
            newZ++;
            newItems[selectingId] = { ...targetItem, parentId: parentGroup.id, z: newZ };
            dropChecked = true;
          }
        }
      }
    })

    if (!dropChecked) {
      const parentItem = originList[parentGroup.id];
      if (parentItem) newItems[selectingId] = { ...targetItem, parentId: parentGroup.id, z: dropY - parentItem.order };
    }

    setItems(newItems);
  }

  return (
    <Box>
      <ThemeProvider theme={tabsIndicatorTheme}>
        <TabsAppBar position="static" elevation={0}>
          <Tabs value={tabValue} onChange={handleChange} variant="fullWidth">
            <Tab label="アイテム" {...a11yProps(0)} />
            <Tab label="詳細設定" {...a11yProps(1)} />
          </Tabs>
        </TabsAppBar>
        <TabPanel value={tabValue} index={0}>
          <Container style={{height: workHeight, overflow: 'auto'}}>
          { ids.length > 0 &&
            <SelectUiItemName cardId={cardId} ids={ids}
              originList={originList} list={list} setList={setList}
              selectingId={selectingId} setSelectingId={setSelectingId}
              changeItemZ={changeItemZ} />
          }
          </Container>
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <Container style={{height: workHeight-10, overflow: 'auto'}}>
            { (ids.length > 0 && selectingItem !== null) &&
              <ItemSetArea id={selectingId} uiItem={selectingItem} changeItem={changeItem} deleteItem={deleteItem} />
            }
          </Container>
        </TabPanel>
      </ThemeProvider>
    </Box>
  );
}

//デザインタブ-詳細タブ
type ItemSetAreaType = {
  id?: string;
  uiItem: UiCardItemDb;
  changeItem: (item: UiCardItemDb | null, id?: string) => void;
  deleteItem: () => void;
}
const ItemSetArea: React.FC<ItemSetAreaType> = ({id, uiItem, changeItem, deleteItem}) => {
  return (
    <>
      {(uiItem.type === 'input') && <InputItemDetail uiItem={uiItem} changeItem={changeItem} font={uiItem.labelFont} />}
      {(uiItem.type === 'multi') && <MultiInputItemDetail uiItem={uiItem} changeItem={changeItem} font={uiItem.labelFont} />}
      {(uiItem.type === 'text') && <TextItemDetail uiItem={uiItem} changeItem={changeItem} font={uiItem.textFont}  labelFont={uiItem.labelFont}/>}
      {(uiItem.type === 'button') && <ButtonItemDetail uiItem={uiItem} changeItem={changeItem} font={uiItem.labelFont} />}
      {(uiItem.type === 'checkbox') && <CheckboxItemDetail uiItem={uiItem} changeItem={changeItem} font={uiItem.labelFont}/>}
      {(uiItem.type === 'dropdown' && typeof id === 'string') && <DropdownItemDetail id={id} uiItem={uiItem} changeItem={changeItem} font={uiItem.labelFont} />}
      {(uiItem.type === 'variable') && <VariableImageItemDetail uiItem={uiItem} changeItem={changeItem} />}
      {(uiItem.type === 'static') && <StaticImageItemDetail uiItem={uiItem} changeItem={changeItem} />}
      {(uiItem.type === 'group') && <GroupItemDetail uiItem={uiItem} changeItem={changeItem} />}
      <Box mt={2}>
        <Button color='primary' variant='text' onClick={deleteItem}>この項目を削除する</Button>
      </Box>
    </>
  );
}

type LabelFieldProps = {
  uiItem: InputUiSketchItem | TextUiSketchItem | ButtonUiSketchItem | CheckboxUiSketchItem | DropdownUiSketchItem | MultiInputUiSketchItem;
  changeItem: (item: UiCardItemDb | null) => void;
}
const LabelField: React.FC<LabelFieldProps> = ({uiItem, changeItem}) => {
  const changeLabel = (value: string) => {
    const newItem = deepCopy(uiItem);
    newItem.label = value;
    changeItem(newItem);
  }

  return (
    <ItemField variant="outlined"
      label="ラベル"
      value={uiItem.label}
      onChange={(ev) => changeLabel(ev.target.value)}
    />
  );
}

type LabelVisibilityFieldProps = {
  uiItem: InputUiSketchItem | TextUiSketchItem | CheckboxUiSketchItem | DropdownUiSketchItem | MultiInputUiSketchItem;
  changeItem: (item: UiCardItemDb | null) => void;
}
const LabelVisibilityField: React.FC<LabelVisibilityFieldProps> = ({uiItem, changeItem}) => {
  const changeLabelVisibility = (value: boolean) => {
    const newItem = deepCopy(uiItem);
    newItem.labelVisibility = value;
    changeItem(newItem);
  }

  return (
    <FormControlLabel label="ラベル表示"
    control={
      <Checkbox color="default"
        checked={uiItem.labelVisibility} onChange={(ev, checked) => changeLabelVisibility(checked)}
      /> }
    />
  );
}

type RadiusFieldProps = {
  uiItem: VariableImageUiSketchItem | StaticImageUiSketchItem;
  changeItem: (item: UiCardItemDb | null) => void;
}
const RadiusField: React.FC<RadiusFieldProps> = ({uiItem, changeItem}) => {
  const changeRadius = (value: number) => {
    let radius = value;
    if (radius > 100) radius = 100;
    if (radius < 0) radius = 0;
    const newItem = deepCopy(uiItem);
    newItem.radius = radius;
    changeItem(newItem);
  }

  return (
    <TextField variant='outlined' label='角丸'
      type="number" inputProps={{min: 0, max: 100, step: 1}}
      value={uiItem.radius}
      onChange={(ev) => changeRadius(Number(ev.target.value))}
    />
  );
}

type IsButtonFieldProps = {
  uiItem: VariableImageUiSketchItem | StaticImageUiSketchItem;
  changeItem: (item: UiCardItemDb | null) => void;
}
const IsButtonField: React.FC<IsButtonFieldProps> = ({uiItem, changeItem}) => {
  const changeIsButton = (value: boolean) => {
    const newItem = deepCopy(uiItem);
    newItem.button = value;
    changeItem(newItem);
  }

  return (
    <FormControlLabel label="ボタン化"
    control={
      <Checkbox color="default"
        checked={uiItem.button} onChange={(ev, checked) => changeIsButton(checked)}
      /> }
    />
  );
}

type NotesFieldProps = {
  uiItem: UiCardItemDb;
  changeItem: (item: UiCardItemDb | null) => void;
}
const NotesField: React.FC<NotesFieldProps> = ({uiItem, changeItem}) => {
  const changeNotes = (value: string) => {
    const newItem = deepCopy(uiItem);
    newItem.notes = value;
    changeItem(newItem);
  }

  return (
    <ItemField label="概要"
      variant="outlined" multiline rows={4}
      value={uiItem.notes}
      onChange={(ev) => changeNotes(ev.target.value)}
    />
  );
}

type InputItemDetailProps = {
  uiItem: InputUiSketchItem;
  changeItem: (item: UiCardItemDb | null) => void;
  font: FontType;
}
//テキスト入力項目　詳細タブ
const InputItemDetail: React.FC<InputItemDetailProps> = ({uiItem, changeItem, font}) => {
  const changePlaceholder = (value: string) => {
    const newItem = deepCopy(uiItem);
    newItem.placeholder = value;
    changeItem(newItem);
  }
  const fontUpdate = (newFont: FontType) => {
    const newItem = deepCopy(uiItem);
    newItem.labelFont = newFont;
    changeItem(newItem);
  }

  return (
    <Box mt={2}>
      <ItemNameInput item={uiItem} changeItem={changeItem} />
      <Box mt={3}>
        <LabelField uiItem={uiItem} changeItem={changeItem} />
      </Box>
      <Box mt={3}>
        <LabelVisibilityField uiItem={uiItem} changeItem={changeItem} />
      </Box>
      <Box mt={3}>
        <EditItemFont kind="uiInput" font={font} onUpdate={fontUpdate} />
      </Box>
      <Box mt={3}>
        <ItemField variant="outlined"
          label="プレイスホルダー"
          value={uiItem.placeholder}
          onChange={(ev) => changePlaceholder(ev.target.value)}
        />
      </Box>
      <Box mt={3}>
        <EditItemPosition item={uiItem} changeItem={changeItem} />
      </Box>
      <Box mt={3}>
        <NotesField uiItem={uiItem} changeItem={changeItem} />
      </Box>
    </Box>
  );
}

type TextItemDetailProps = {
  uiItem: TextUiSketchItem;
  changeItem: (item: UiCardItemDb | null) => void;
  font: FontType;
  labelFont: FontType;
}
//テキスト表示項目　詳細タブ
const TextItemDetail: React.FC<TextItemDetailProps> = ({uiItem, changeItem, font, labelFont}) => {
  const changeText = (value: string) => {
    const newItem = deepCopy(uiItem);
    newItem.text = value;
    changeItem(newItem);
  }
  const fontUpdate = (newFont: FontType) => {
    const newItem = deepCopy(uiItem);
    newItem.textFont = newFont;
    changeItem(newItem);
  }
  const labelFontUpdate = (newFont: FontType) => {
    const newItem = deepCopy(uiItem);
    newItem.labelFont = newFont;
    changeItem(newItem);
  }
  const changeFlame = (value: boolean) => {
    const newItem = deepCopy(uiItem);
    newItem.flame = value;
    changeItem(newItem);
  }
  const changeFlameColor = (value: string) => {
    const newItem = deepCopy(uiItem);
    newItem.flameColor = value;
    changeItem(newItem);
  }
  const changeBackgroundOpacity = (value: number) => {
    let opacity = value;
    if (value < 0) opacity = 0;
    else if (value > 1) opacity = 1;
    const newItem = deepCopy(uiItem);
    newItem.backgroundOpacity = opacity;
    changeItem(newItem);
  }
  const changeBackgroundColor = (value: string) => {
    const newItem = deepCopy(uiItem);
    newItem.backgroundColor = value;
    changeItem(newItem);
  }

  return (
    <Box mt={2}>
      <ItemNameInput item={uiItem} changeItem={changeItem} />
      <Box mt={3}>
        <ItemField variant="outlined"
          label="テキスト"
          value={uiItem.text}
          onChange={(ev) => changeText(ev.target.value)}
        />
      </Box>
      <Box mt={3}>
        <EditItemFont kind="uiText" font={font} onUpdate={fontUpdate} />
      </Box>
      <Box mt={3}>
        <LabelField uiItem={uiItem} changeItem={changeItem} />
      </Box>
      <Box mt={3}>
        <LabelVisibilityField uiItem={uiItem} changeItem={changeItem} />
      </Box>
      <Box mt={3}>
        <EditItemFont kind="uiTextLabel" font={labelFont} onUpdate={labelFontUpdate} />
      </Box>
      <Box mt={3}>
        枠線
        <Grid container alignItems="center">
          <Grid item xs={6}>
            <FormControlLabel label="表示"
              control={
                <Checkbox checked={uiItem.flame} color="primary"
                  onChange={(ev) => changeFlame(ev.currentTarget.checked)} />
              } />
          </Grid>
          <Grid item xs={6}>
            <ColorPalette
              currentValue={uiItem.flameColor}
              onChange={changeFlameColor}
              disabled={!uiItem.flame}
            />
          </Grid>
        </Grid>
      </Box>
      <Box mt={3}>
        塗りつぶし
        <Grid container alignItems="center" style={{marginTop: '8px'}}>
          <Grid item xs={6}>
            <TextField variant='outlined' label='透明度'
              type="number" inputProps={{min: 0, max: 1, step: 0.1}}
              value={uiItem.backgroundOpacity}
              onChange={(ev) => changeBackgroundOpacity(Number(ev.target.value))}
            />
          </Grid>
          <Grid item xs={6}>
            <ColorPalette
              currentValue={uiItem.backgroundColor}
              onChange={changeBackgroundColor}
            />
          </Grid>
        </Grid>
      </Box>
      <Box mt={3}>
        <EditItemPosition item={uiItem} changeItem={changeItem} />
      </Box>
      <Box mt={3}>
        <NotesField uiItem={uiItem} changeItem={changeItem} />
      </Box>
    </Box>
  );
}

type ButtonItemDetailProps = {
  uiItem: ButtonUiSketchItem;
  changeItem: (item: UiCardItemDb | null) => void;
  font: FontType;
}
//ボタン項目　詳細タブ
const ButtonItemDetail: React.FC<ButtonItemDetailProps> = ({uiItem, changeItem, font}) => {
  const fontUpdate = (newFont: FontType) => {
    const newItem = deepCopy(uiItem);
    newItem.labelFont = newFont;
    changeItem(newItem);
  }
  const changeBackgroundColor = (value: string) => {
    const newItem = deepCopy(uiItem);
    newItem.backgroundColor = value;
    changeItem(newItem);
  }

  return (
    <Box mt={2}>
      <ItemNameInput item={uiItem} changeItem={changeItem} />
      <Box mt={3}>
        <LabelField uiItem={uiItem} changeItem={changeItem} />
      </Box>
      <Box mt={3}>
        <EditItemFont kind="uiButton" font={font} onUpdate={fontUpdate} />
      </Box>
      <Box mt={3}>
        <ColorPalette
          currentValue={uiItem.backgroundColor}
          onChange={changeBackgroundColor}
          label="背景色"
        />
      </Box>
      <Box mt={3}>
        <EditItemPosition item={uiItem} changeItem={changeItem} />
      </Box>
      <Box mt={3}>
        <NotesField uiItem={uiItem} changeItem={changeItem} />
      </Box>
    </Box>
  );
}
type CheckboxItemDetailProps = {
  uiItem: CheckboxUiSketchItem;
  changeItem: (item: UiCardItemDb | null) => void;
  font: FontType;
}
//チェックボックス項目　詳細タブ
const CheckboxItemDetail: React.FC<CheckboxItemDetailProps> = ({uiItem, changeItem, font}) => {
  const fontUpdate = (newFont: FontType) => {
    const newItem = deepCopy(uiItem);
    newItem.labelFont = newFont;
    changeItem(newItem);
  }

  return (
    <Box mt={2}>
      <ItemNameInput item={uiItem} changeItem={changeItem} />
      <Box mt={3}>
        <LabelField uiItem={uiItem} changeItem={changeItem} />
      </Box>
      <Box mt={3}>
        <LabelVisibilityField uiItem={uiItem} changeItem={changeItem} />
      </Box>
      <Box mt={3}>
        <EditItemFont kind="uiInput" font={font} onUpdate={fontUpdate} />
      </Box>
      <Box mt={3}>
        <EditItemPosition item={uiItem} changeItem={changeItem} />
      </Box>
      <Box mt={3}>
        <NotesField uiItem={uiItem} changeItem={changeItem} />
      </Box>
    </Box>
  );
}

type DropdownItemDetailProps = {
  id: string;
  uiItem: DropdownUiSketchItem;
  changeItem: (item: UiCardItemDb | null, id?: string) => void;
  font: FontType;
}
// ドロップダウン項目　詳細タブ
const DropdownItemDetail: React.FC<DropdownItemDetailProps> = ({id, uiItem, changeItem, font}) => {
  const optionIds = Object.keys(uiItem.options);
  const options = optionIds.map(id => uiItem.options[id]);
  const [newOptions, setNewOptions] = useState('');
  type TargetType = { id: string; value: DropdownUiSketchItem; }
  const [target, setTarget] = useState<TargetType | undefined>(undefined);
  const [composing, setComposing] = useState(false);

  const changeOptions = (ev: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>, ix: number) => {
    const value = ev.currentTarget.value;
    const newItem = deepCopy(uiItem);
    newItem.options[optionIds[ix]] = value;
    changeItem(newItem);
    setTarget(undefined);
  }
  const deleteOption = (ev: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>, ix: number) => {
    const value = ev.currentTarget.value;
    const newItem = deepCopy(uiItem);
    if (value === '') delete newItem.options[optionIds[ix]];
    changeItem(newItem);
    setTarget(undefined);
  }
  const changeNewOption = (value: string) => {
    setTarget({id: id, value: uiItem});
    setNewOptions(value);
  }
  const addOption = () => {
    if (newOptions === '' || target === undefined) return;
    const newItem = deepCopy(target.value);
    const newId = nanoid();
    newItem.options[newId] = newOptions;
    changeItem(newItem, target.id);
    setNewOptions('');
    setTarget(undefined);
  }
  const fontUpdate = (newFont: FontType) => {
    const newItem = deepCopy(uiItem);
    newItem.labelFont = newFont;
    changeItem(newItem);
  }

  return (
    <Box mt={2}>
      <ItemNameInput item={uiItem} changeItem={changeItem} />
      <Box mt={3}>
        <LabelField uiItem={uiItem} changeItem={changeItem} />
      </Box>
      <Box mt={3}>
        <LabelVisibilityField uiItem={uiItem} changeItem={changeItem} />
      </Box>
      <Box mt={3}>
        <EditItemFont kind="uiInput" font={font} onUpdate={fontUpdate} />
      </Box>
      <Box mt={3}>
        選択肢
        <Box mt={1}>
          { options.map((columnName, ix) =>
            <TextField margin="dense" variant="outlined" value={columnName}
              style={{width: '70%'}}
              onChange={(ev) => changeOptions(ev, ix)}
              onBlur={(ev) => deleteOption(ev, ix)} />
          ) }
          <TextField margin="dense" variant="outlined" placeholder="選択肢を追加します"
            value={newOptions} style={{width: '70%'}}
            onFocus={() => setTarget({id: id, value: uiItem})}
            onChange={(ev) => changeNewOption(ev.currentTarget.value)}
            onBlur={() => addOption()}
            onCompositionStart={() => setComposing(true)}
            onCompositionEnd={() => setComposing(false)}
            onKeyDown={(ev) => ev.keyCode === 13 && !composing ? addOption() : undefined} />
        </Box>
      </Box>
      <Box mt={5}>
        <EditItemPosition item={uiItem} changeItem={changeItem} />
      </Box>
      <Box mt={3}>
        <NotesField uiItem={uiItem} changeItem={changeItem} />
      </Box>
    </Box>
  );
}
type MutliInputItemDetailProps = {
  uiItem: MultiInputUiSketchItem;
  changeItem: (item: UiCardItemDb | null) => void;
  font: FontType;
}
//テキスト入力項目　詳細タブ
const MultiInputItemDetail: React.FC<MutliInputItemDetailProps> = ({uiItem, changeItem, font}) => {
  const changePlaceholder = (value: string) => {
    const newItem = deepCopy(uiItem);
    newItem.placeholder = value;
    changeItem(newItem);
  }
  const fontUpdate = (newFont: FontType) => {
    const newItem = deepCopy(uiItem);
    newItem.labelFont = newFont;
    changeItem(newItem);
  }

  return (
    <Box mt={2}>
      <ItemNameInput item={uiItem} changeItem={changeItem} />
      <Box mt={3}>
        <LabelField uiItem={uiItem} changeItem={changeItem} />
      </Box>
      <Box mt={3}>
        <LabelVisibilityField uiItem={uiItem} changeItem={changeItem} />
      </Box>
      <Box mt={3}>
        <EditItemFont kind="uiInput" font={font} onUpdate={fontUpdate} />
      </Box>
      <Box mt={3}>
        <ItemField variant="outlined"
          label="プレイスホルダー"
          value={uiItem.placeholder}
          onChange={(ev) => changePlaceholder(ev.target.value)}
        />
      </Box>
      <Box mt={3}>
        <EditItemPosition item={uiItem} changeItem={changeItem} />
      </Box>
      <Box mt={3}>
        <NotesField uiItem={uiItem} changeItem={changeItem} />
      </Box>
    </Box>
  );
}

type VariableImageItemDetailProps = {
  uiItem: VariableImageUiSketchItem;
  changeItem: (item: UiCardItemDb | null) => void;
}
//可変画像項目　詳細タブ
const VariableImageItemDetail: React.FC<VariableImageItemDetailProps> = ({uiItem, changeItem}) => {
  return (
    <Box mt={2}>
      <ItemNameInput item={uiItem} changeItem={changeItem} />
      <Box mt={3}>
        <RadiusField uiItem={uiItem} changeItem={changeItem} />
      </Box>
      <Box mt={3}>
        <IsButtonField uiItem={uiItem} changeItem={changeItem} />
      </Box>
      <Box mt={3}>
        <EditItemPosition item={uiItem} changeItem={changeItem} />
      </Box>
      <Box mt={3}>
        <NotesField uiItem={uiItem} changeItem={changeItem} />
      </Box>
    </Box>
  );
}

type StaticImageItemDetailProps = {
  uiItem: StaticImageUiSketchItem;
  changeItem: (item: UiCardItemDb | null) => void;
}
//固定画像項目　詳細タブ
const StaticImageItemDetail: React.FC<StaticImageItemDetailProps> = ({uiItem, changeItem}) => {
  return (
    <Box mt={2}>
      <ItemNameInput item={uiItem} changeItem={changeItem} />
      <Box mt={3}>
        <RadiusField uiItem={uiItem} changeItem={changeItem} />
      </Box>
      <Box mt={3}>
        <IsButtonField uiItem={uiItem} changeItem={changeItem} />
      </Box>
      <Box mt={3}>
        <EditItemPosition item={uiItem} changeItem={changeItem} />
      </Box>
      <Box mt={3}>
        <NotesField uiItem={uiItem} changeItem={changeItem} />
      </Box>
    </Box>
  );
}

type GroupItemDetailProps = {
  uiItem: GroupUiSketchItem;
  changeItem: (item: UiCardItemDb | null) => void;
}
//グループ項目　詳細タブ
const GroupItemDetail: React.FC<GroupItemDetailProps> = ({uiItem, changeItem}) => {
  return (
    <Box mt={2}>
      <ItemNameInput item={uiItem} changeItem={changeItem} />
      <Box mt={3}>
        <EditItemPosition item={uiItem} changeItem={changeItem} />
      </Box>
      <Box mt={3}>
        <NotesField uiItem={uiItem} changeItem={changeItem} />
      </Box>
    </Box>
  );
}

//デザインタブ
type DesignUiCardProps = {
  cardId: string;
  card: CardDb;
  setCard: (card: CardDb) => void;
  uiItems: EditUiCardItemDbHashType;
  setUiItems: (items: EditUiCardItemDbHashType) => void;
}
const DesignUiCard: React.FC<DesignUiCardProps> = ({cardId, card, setCard, uiItems, setUiItems}) => {
  const windowSize = useWindowSize();
  const workHeight = windowSize.innerHeight - (64 * 2) - 80;
  const ids = Object.keys(uiItems).filter(id => uiItems[id]?.parentId === cardId);
  const [selectingId, setSelectingId] = useState<string | undefined>(undefined);
  const [imageBrowserAction, setImageBrowserAction] = useState({show: false});
  const [drawToolAction, setDrawToolAction] = useState(false);
  const [itemDesignTab, setItemDesignTab] = useState(0);

  const addNewItem = (kind: string, image?: FullSizeImageAddType) => {
    const newItems = deepCopy(uiItems);
    const newId = generateUiCardItemId();
    const newItem: EditUiCardItemDbHashType = {};
    if (kind === 'input') {
      newItem[newId] = {
        cardId: cardId, parentId: cardId,
        type: 'input', name: 'new input',
        x: 0, y: 0, z: ids.length, width: 300, height: 64,
        notes: '',
        label: 'new input', labelVisibility: true, labelFont: {...initial.uiTextFont, size: 16},
        placeholder: 'プレイスホルダー'
      }
      Object.assign(newItems, newItem);
    } else if (kind === 'text') {
      newItem[newId] = {
        cardId: cardId, parentId: cardId,
        type: 'text', name: 'new text',
        x: 0, y: 0, z: ids.length, width: 300, height: 64,
        notes: '',
        text: '入力してください', textFont: initial.uiTextFont,
        label: 'new text', labelVisibility: true, labelFont: {...initial.font, size: 16},
        flame: false, flameColor: '#000000',
        backgroundColor: '#FFFFFF', backgroundOpacity: 0
      }
      Object.assign(newItems, newItem);
    } else if (kind === 'button') {
      newItem[newId] = {
        cardId: cardId, parentId: cardId,
        type: 'button', name: 'new button',
        x: 0, y: 0, z: ids.length, width: 200, height: 64,
        notes: '',
        label: 'new button', labelFont: initial.uiTextFont,
        backgroundColor: '#FFFFFF'
      }
      Object.assign(newItems, newItem);
    } else if (kind === 'checkbox') {
      newItem[newId] = {
        cardId: cardId, parentId: cardId,
        type: 'checkbox', name: 'new checkbox',
        x: 0, y: 0, z: ids.length, width: 300, height: 42,
        notes: '',
        label: 'new checkbox', labelVisibility: true, labelFont: initial.uiTextFont
      }
      Object.assign(newItems, newItem);
    } else if (kind === 'dropdown') {
      newItem[newId] = {
        cardId: cardId, parentId: cardId,
        type: 'dropdown', name: 'new dropdown',
        x: 0, y: 0, z: ids.length, width: 300, height: 76,
        notes: '',
        label: 'new dropdown', labelVisibility: true, labelFont: initial.uiTextFont,
        options: {}
      }
      Object.assign(newItems, newItem);
    } else if (kind === 'multi') {
      newItem[newId] = {
        cardId: cardId, parentId: cardId,
        type: 'multi', name: 'new multi input',
        x: 0, y: 0, z: ids.length, width: 300, height: 200,
        notes: '',
        label: 'new multi input', labelVisibility: true, labelFont: {...initial.uiTextFont, size: 16},
        placeholder: 'プレイスホルダー'
      }
      Object.assign(newItems, newItem);
    } else if (kind === 'variable') {
      newItem[newId] = {
        cardId: cardId, parentId: cardId,
        type: 'variable', name: 'new variable',
        x: 0, y: 0, z: ids.length, width: 300, height: 200,
        notes: '',
        button: false, radius: 0
      }
      Object.assign(newItems, newItem);
    } else if (kind === 'static' && image) {
      newItem[newId] = {
        cardId: cardId, parentId: cardId,
        type: 'static', name: 'new static',
        x: 0, y: 0, z: ids.length, width: 200, height: 200,
        notes: '',
        imageId: image.id, button: false, radius: 0
      }
      Object.assign(newItems, newItem);
    } else if (kind === 'group') {
      newItem[newId] = {
        cardId: cardId, parentId: cardId,
        type: 'group', name: 'new group',
        x: 0, y: 0, z: ids.length, width: 200, height: 200,
        notes: ''
      }
      Object.assign(newItems, newItem);
    } else return;

    setUiItems(newItems);
    setSelectingId(newId);
  }
  const changeItem = (uiItem: UiCardItemDb | null, id?: string) => {
    if (typeof selectingId !== 'string') return;
    const targetId = id ? id : selectingId;
    const newItems = deepCopy(uiItems);
    newItems[targetId] = uiItem;
    setUiItems(newItems);
  }
  const changeTab = () => {
    setItemDesignTab(1);
  }

  return(
    <>
      <Box>
        <Grid container spacing={0}>
          <Grid item xs={8}>
            <Grid container spacing={0}>
              <Grid item xs={1}>
                <Box style={{height: workHeight, overflow: 'auto'}}>
                  <ItemMenuArea addNewItem={addNewItem} onImageBrowser={() => setImageBrowserAction({show:true})}
                    onDrawTool={() => setDrawToolAction(true)} />
                </Box>
              </Grid>
              <Grid item xs={11}>
                <Box style={{height: workHeight, overflow: 'auto'}}>
                  <CardDesignArea cardId={cardId}
                    card={card} setCard={setCard}
                    items={uiItems} changeItem={changeItem}
                    selectingId={selectingId} setSelectingId={setSelectingId}
                    changeTab={changeTab}
                  />
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={4}>
            <ItemDesignArea cardId={cardId}
              items={uiItems} setItems={setUiItems} changeItem={changeItem}
              selectingId={selectingId} setSelectingId={setSelectingId}
              tabValue={itemDesignTab}
              setTabValue={setItemDesignTab}
            />
          </Grid>
        </Grid>
      </Box>
      <ImageBrowserDialog 
        open={imageBrowserAction.show}
        onAddAttribute={(kind, image) => {
          addNewItem('static', image);
          setImageBrowserAction({show: false});
        }}
        onClose={() => setImageBrowserAction({show: false})} 
      />
      <DrawToolDialog open={drawToolAction} onClose={() => setDrawToolAction(false)}
        onSave={(image: FullSizeImageAddType) => {
          setDrawToolAction(false);
          addNewItem('static', image);
        }}
      />
    </>
  );
}

export default DesignUiCard;
